import type { ReferenceDataField } from '@moment/trading-types/instrument-data'

import {
  type MoodysRating,
  SPRating,
} from '@moment/trading-types/reference-data'
import { z } from 'zod'

import {
  type AverageValueValidationResult,
  type ExtendedAverageValueConstraint,
  ModelConstraintType,
} from '@/types/api/constraints'
import { ValueUnit } from '@/types/enums/value-unit'
import { compareMoodysRatings } from '@/utils/moodysRatings'
import { compareSpRatings } from '@/utils/spRatings'

import { constraintPrioritySchema } from './helpers'

/**
 * Check if a field is an SP rating field (SP Rating or Lowest Rating)
 * Handles both enum values and string literals
 * Note: Enum values are the same as string literals (e.g., 'sp_rating', 'lowest_rating')
 */
export const isSpRatingField = (field: string | null | undefined): boolean => {
  if (!field) return false
  return field === 'sp_rating' || field === 'lowest_rating'
}

/**
 * Check if a field is a Moody's rating field
 * Handles both enum values and string literals
 * Note: Enum values are the same as string literals (e.g., 'moodys_rating')
 */
export const isMoodysRatingField = (
  field: string | null | undefined,
): boolean => {
  if (!field) return false
  return field === 'moodys_rating'
}

/**
 * Check if a field is any rating field (SP Rating, Moody's Rating, or Lowest Rating)
 * Handles both enum values and string literals
 */
export const isRatingField = (field: string | null | undefined): boolean => {
  return isSpRatingField(field) || isMoodysRatingField(field)
}

/**
 * Zod schema for average value constraint validation
 */
const averageValueConstraintSchema = z.object({
  type: z.literal(ModelConstraintType.AverageValue),
  field: z.custom<ReferenceDataField>(
    (val) => typeof val === 'string' && val.length > 0,
    {
      message: 'Please select a data field',
    },
  ),
  min_average_value: z
    .union([
      z.number(),
      z.nativeEnum(SPRating),
      z.string().min(1, 'Minimum value is required'),
      z.null(),
    ])
    .optional()
    .nullable(),
  target_average_value: z
    .union([
      z.number(),
      z.nativeEnum(SPRating),
      z.string().min(1, 'Target value is required'),
      z.null(),
    ])
    .optional()
    .nullable(),
  max_average_value: z
    .union([
      z.number(),
      z.nativeEnum(SPRating),
      z.string().min(1, 'Maximum value is required'),
      z.null(),
    ])
    .optional()
    .nullable(),
  constraint_priority: constraintPrioritySchema,
  id: z.string().default(''),
  unit: z.union([z.nativeEnum(ValueUnit), z.string()]).optional(),
}) as z.ZodType<ExtendedAverageValueConstraint>

/**
 * Validates the relationships between min, target, and max values for numeric values
 */
function validateNumericRelationships(
  min?: number | null,
  target?: number | null,
  max?: number | null,
): { valid: boolean; error?: string; errors?: string[] } {
  const errors: string[] = []

  // Only validate relationships if both values are present
  if (
    min !== undefined &&
    min !== null &&
    target !== undefined &&
    target !== null
  ) {
    // Check min <= target
    if (min > target) {
      errors.push('Minimum value must be less than or equal to target value')
    }
  }

  if (
    target !== undefined &&
    target !== null &&
    max !== undefined &&
    max !== null
  ) {
    // Check target <= max
    if (target > max) {
      errors.push('Target value must be less than or equal to maximum value')
    }
  }

  // Check min <= max directly if both exist
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    if (min > max) {
      errors.push('Minimum value must be less than or equal to maximum value')
    }
  }

  return {
    valid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    errors: errors.length > 0 ? errors : undefined,
  }
}

/**
 * Validates the relationships between min, target, and max values for SP ratings
 */
function validateRatingRelationships(
  min?: SPRating | null,
  target?: SPRating | null,
  max?: SPRating | null,
): { valid: boolean; error?: string; errors?: string[] } {
  const errors: string[] = []

  // Only validate relationships if both values are present
  if (
    min !== undefined &&
    min !== null &&
    target !== undefined &&
    target !== null
  ) {
    // Check min <= target (for SP ratings, a "higher" rating has a lower numerical value)
    const comparisonMinTarget = compareSpRatings(min, target)
    if (comparisonMinTarget < 0) {
      errors.push('Minimum rating must be less than or equal to target rating')
    }
  }

  if (
    target !== undefined &&
    target !== null &&
    max !== undefined &&
    max !== null
  ) {
    // Check target <= max
    const comparisonTargetMax = compareSpRatings(target, max)
    if (comparisonTargetMax < 0) {
      errors.push('Target rating must be less than or equal to maximum rating')
    }
  }

  // Check min <= max directly if both exist
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    const comparisonMinMax = compareSpRatings(min, max)
    if (comparisonMinMax < 0) {
      errors.push('Minimum rating must be less than or equal to maximum rating')
    }
  }

  return {
    valid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    errors: errors.length > 0 ? errors : undefined,
  }
}

function validateMoodysRatingRelationships(
  min?: MoodysRating | null,
  target?: MoodysRating | null,
  max?: MoodysRating | null,
): { valid: boolean; error?: string; errors?: string[] } {
  const errors: string[] = []

  // Only validate relationships if both values are present
  if (
    min !== undefined &&
    min !== null &&
    target !== undefined &&
    target !== null
  ) {
    // Check min <= target (for SP ratings, a "higher" rating has a lower numerical value)
    const comparisonMinTarget = compareMoodysRatings(min, target)
    if (comparisonMinTarget < 0) {
      errors.push('Minimum rating must be less than or equal to target rating')
    }
  }

  if (
    target !== undefined &&
    target !== null &&
    max !== undefined &&
    max !== null
  ) {
    // Check target <= max
    const comparisonTargetMax = compareMoodysRatings(target, max)
    if (comparisonTargetMax < 0) {
      errors.push('Target rating must be less than or equal to maximum rating')
    }
  }

  // Check min <= max directly if both exist
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    const comparisonMinMax = compareMoodysRatings(min, max)
    if (comparisonMinMax < 0) {
      errors.push('Minimum rating must be less than or equal to maximum rating')
    }
  }

  return {
    valid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    errors: errors.length > 0 ? errors : undefined,
  }
}
/**
 * Validates an average value constraint
 */
export function validateAverageValueConstraint(
  constraint: ExtendedAverageValueConstraint,
): AverageValueValidationResult {
  try {
    const errors: string[] = []

    // Preprocess values - treat empty strings as undefined
    // but keep zero values as valid for percentages
    const processedConstraint = {
      ...constraint,
      min_average_value:
        constraint.min_average_value === '' ||
        constraint.min_average_value === undefined
          ? null
          : constraint.min_average_value,
      target_average_value:
        constraint.target_average_value === '' ||
        constraint.target_average_value === undefined
          ? null
          : constraint.target_average_value,
      max_average_value:
        constraint.max_average_value === '' ||
        constraint.max_average_value === undefined
          ? null
          : constraint.max_average_value,
    }

    // Validate using Zod schema
    const result = averageValueConstraintSchema.safeParse(processedConstraint)
    if (!result.success) {
      // Get all zod errors
      result.error.errors.forEach((err) => {
        errors.push(err.message)
      })

      return {
        valid: false,
        errors,
      }
    }

    const {
      min_average_value,
      target_average_value,
      max_average_value,
      field,
    } = processedConstraint

    // Check if at least one value is specified
    const hasMin = min_average_value !== undefined && min_average_value !== null
    const hasTarget =
      target_average_value !== undefined && target_average_value !== null
    const hasMax = max_average_value !== undefined && max_average_value !== null

    // Validate that at least one value is specified
    if (!hasMin && !hasMax && !hasTarget) {
      errors.push('Minimum, target, or maximum value must be specified')
    } else if (hasTarget && !hasMin && !hasMax) {
      errors.push(
        'Minimum or maximum value must be specified with target value',
      )
    }

    // Check if values are SP ratings
    const isSpRating = isSpRatingField(field)
    const isMoodys = isMoodysRatingField(field)
    // Validate relationships between values
    // biome-ignore lint/suspicious/noImplicitAnyLet: Bulk suppression as part of biome migration
    let relationshipValidation

    if (isSpRating) {
      relationshipValidation = validateRatingRelationships(
        min_average_value as SPRating,
        target_average_value as SPRating,
        max_average_value as SPRating,
      )
    } else if (isMoodys) {
      relationshipValidation = validateMoodysRatingRelationships(
        min_average_value as MoodysRating,
        target_average_value as MoodysRating,
        max_average_value as MoodysRating,
      )
    } else {
      relationshipValidation = validateNumericRelationships(
        min_average_value as number,
        target_average_value as number,
        max_average_value as number,
      )
    }

    if (!relationshipValidation.valid) {
      // Add all relationship validation errors
      if (
        relationshipValidation.errors &&
        relationshipValidation.errors.length > 0
      ) {
        errors.push(...relationshipValidation.errors)
      } else if (relationshipValidation.error) {
        errors.push(relationshipValidation.error)
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      relationshipsValid: relationshipValidation.valid,
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown validation error'

    return {
      valid: false,
      errors: [errorMessage],
    }
  }
}

/**
 * Validator for average value constraints
 */
export const averageValueValidator: {
  validate: (
    constraint: ExtendedAverageValueConstraint,
  ) => AverageValueValidationResult
  schema: z.ZodType<ExtendedAverageValueConstraint>
} = {
  validate: validateAverageValueConstraint,
  schema: averageValueConstraintSchema,
}
